import { formatData } from '@hyphengroup/cms-components/utils/helpers/formatDate';
import type { PolicySummaryResponse } from '$/types/travelInsurance';

/**
 * Value formatters for the Policy Summary Card resolver (CMS-1821). Row LABELS
 * come from the Strapi field-config; these format the VALUES from the purchase
 * (currency by market, dates/travellers by locale) — ported from the current
 * HubSpot success page so the migrated card reads identically.
 */

export type Market = 'singsaver' | 'moneyhero';

const isZh = (lang: string): boolean => lang.toLowerCase().startsWith('zh');

// SG uses "S$", HK uses "HK$" (display prefix, per Figma), CMS-1821 currency.
const CURRENCY_PREFIX: Record<Market, string> = {
  singsaver: 'S$',
  moneyhero: 'HK$',
};

// Code-owned value wording (EN + ZH-HK), following the CMS-1864 i18n copy.
const VALUE_I18N = {
  adultOne: { en: 'Adult', zh: '位成人' },
  adultMany: { en: 'Adults', zh: '位成人' },
  childOne: { en: 'Child', zh: '位兒童' },
  childMany: { en: 'Children', zh: '位兒童' },
  listSeparator: { en: ', ', zh: '，' },
  chinaNetworkHospital: {
    en: 'China Network Hospital Admission',
    zh: '中國網絡醫院住院',
  },
  coverSingle: { en: 'Single Trip', zh: '單次計劃' },
  coverAnnual: { en: 'Annual', zh: '全年旅程' },
} as const;

const t = (key: keyof typeof VALUE_I18N, lang: string): string =>
  isZh(lang) ? VALUE_I18N[key].zh : VALUE_I18N[key].en;

export const formatPremium = (amount: number, market: Market): string =>
  `${CURRENCY_PREFIX[market]} ${amount.toFixed(2)}`;

/** Discount % from base vs collected premium; `undefined` when there is none. */
export const formatDiscountPct = (
  basePremium: number,
  collectionAmount: number,
): string | undefined => {
  const discount = basePremium - collectionAmount;
  if (!(discount > 0) || !(basePremium > 0)) {
    return undefined;
  }
  return `${Math.round((discount / basePremium) * 100)}%`;
};

export const formatPolicyDate = (date: string, lang: string): string =>
  formatData(date, isZh(lang) ? 'yyyy年M月d日' : 'MMM d, yyyy', lang);

export const formatCoverType = (
  // `string`, not `PolicySummaryResponse['planType']` (`TravelInsuranceTypeCode`)
  // — the shared TI/HI purchase base (CMS-1794) types `planType` as a plain
  // `string` since HI's value is `'HOME'`, outside the TI-only enum.
  planType: string,
  lang: string,
  labels?: Record<string, string>,
): string | undefined => {
  const configured = labels?.[planType];
  if (configured) {
    return configured;
  }
  if (planType === 'SGL') {
    return t('coverSingle', lang);
  }
  if (planType === 'ANL') {
    return t('coverAnnual', lang);
  }
  return undefined;
};

// HI-only value wording (EN + ZH-HK), copied verbatim from the HI checkout's
// `constants.ts` (CMS-1794 F3) — that file does not exist on this branch's
// lineage (only on the unmerged INS-6474/staging/260603), so the strings are
// embedded here rather than imported.
const GROSS_AREA_LABELS: Record<string, { en: string; zh: string }> = {
  BELOW_500: { en: '500 sq ft or below', zh: '500 平方呎或以下' },
  FROM_501_TO_750: { en: '501 - 750 sq ft', zh: '501 - 750 平方呎' },
  FROM_751_TO_1000: { en: '751 - 1,000 sq ft', zh: '751 - 1,000 平方呎' },
  FROM_1001_TO_1500: { en: '1,001 - 1,500 sq ft', zh: '1,001 - 1,500 平方呎' },
  FROM_1501_TO_2000: { en: '1,501 - 2,000 sq ft', zh: '1,501 - 2,000 平方呎' },
  ABOVE_2000: { en: 'Above 2,000 sq ft', zh: '2,000 平方呎以上' },
};

const RESIDENCE_TYPE_LABELS: Record<string, { en: string; zh: string }> = {
  OWNER_OCCUPIED_RENTED: { en: 'Owner Occupied/Rented', zh: '自住/租住' },
  FOR_RENT: { en: 'For Rent', zh: '出租' },
};

/** HI-only (built-up area). `labels?.[value]` (Strapi field-config) wins first,
 * else the code fallback above, keyed on the plain string value. */
export const formatBuiltUpArea = (
  grossArea: string | undefined,
  lang: string,
  labels?: Record<string, string>,
): string | undefined => {
  if (!grossArea) {
    return undefined;
  }
  const configured = labels?.[grossArea];
  if (configured) {
    return configured;
  }
  const fallback = GROSS_AREA_LABELS[grossArea];
  return fallback ? (isZh(lang) ? fallback.zh : fallback.en) : undefined;
};

/** HI-only (residence type — bound into the card's COVER_TYPE row, CMS-1794:
 * `planType` is a constant `'HOME'` for HI, not a useful source field).
 * `labels?.[value]` wins first, else the code fallback above. */
export const formatResidenceType = (
  residenceType: string | undefined,
  lang: string,
  labels?: Record<string, string>,
): string | undefined => {
  if (!residenceType) {
    return undefined;
  }
  const configured = labels?.[residenceType];
  if (configured) {
    return configured;
  }
  const fallback = RESIDENCE_TYPE_LABELS[residenceType];
  return fallback ? (isZh(lang) ? fallback.zh : fallback.en) : undefined;
};

const countPart = (
  count: number,
  one: keyof typeof VALUE_I18N,
  many: keyof typeof VALUE_I18N,
  lang: string,
): string => {
  const word = t(count === 1 ? one : many, lang);
  // EN: "1 Adult"; ZH: "1位成人" (no separating space).
  return isZh(lang) ? `${count}${word}` : `${count} ${word}`;
};

export const formatTravellers = (
  purchase: Pick<PolicySummaryResponse, 'noOfAdults' | 'noOfChildren'>,
  lang: string,
): string | undefined => {
  const parts: string[] = [];
  if (purchase.noOfAdults > 0) {
    parts.push(countPart(purchase.noOfAdults, 'adultOne', 'adultMany', lang));
  }
  if (purchase.noOfChildren > 0) {
    parts.push(countPart(purchase.noOfChildren, 'childOne', 'childMany', lang));
  }
  return parts.length ? parts.join(t('listSeparator', lang)) : undefined;
};

export const formatAddOns = (
  // `coverChinaNetworkHospitalAdmission` is optional here (not just on
  // `PolicySummaryResponse`, where it's required) so this also binds HI's
  // `ADD_ONS` row (CMS-1794 F3) — HI has no such concept, so the field is
  // simply absent on `HomePolicySummary`, never `true`.
  purchase: Pick<PolicySummaryResponse, 'selectedAddOns'> &
    Partial<Pick<PolicySummaryResponse, 'coverChinaNetworkHospitalAdmission'>>,
  lang: string,
): string | undefined => {
  const items = [...(purchase.selectedAddOns ?? [])];
  if (purchase.coverChinaNetworkHospitalAdmission) {
    items.push(t('chinaNetworkHospital', lang));
  }
  return items.length ? items.join(t('listSeparator', lang)) : undefined;
};

export const formatDestination = (
  purchase: Pick<PolicySummaryResponse, 'destinations'>,
  // Display labels resolved from insurance reference data by the wrapper (WS1);
  // falls back to the raw destination codes when not supplied.
  destinationLabels: string[] | undefined,
  lang: string,
): string | undefined => {
  const labels =
    destinationLabels && destinationLabels.length
      ? destinationLabels
      : (purchase.destinations ?? []);
  return labels.length ? labels.join(t('listSeparator', lang)) : undefined;
};
